<template>
  <div class="image-upload">
    <div class="upload-area" @click="triggerFileInput" @dragover.prevent @drop.prevent="handleDrop">
      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        multiple
        @change="handleFileSelect"
        style="display: none"
      >
      <div v-if="images.length === 0" class="upload-placeholder">
        <div class="upload-icon">📷</div>
        <h3>点击或拖拽上传图片</h3>
        <p>支持 JPG、PNG、GIF、WEBP 等格式，可选择多张图片</p>
        <p class="size-limit">💡 单个文件建议小于5MB，最大20MB</p>
        <p class="format-info">🔄 支持GIF、PNG等格式，将自动转为JPEG</p>
      </div>
      <div v-else class="image-grid">
        <div v-for="(image, index) in images" :key="index" class="image-item">
          <img :src="image.dataUrl" :alt="`图片 ${index + 1}`" />
          <div class="image-info-overlay">
            <span class="image-size">{{ formatFileSize(image.size) }}</span>
          </div>
          <button class="remove-btn" @click.stop="removeImage(index)">×</button>
        </div>
        <div class="add-more" @click.stop="triggerFileInput">
          <div class="add-icon">+</div>
          <span>添加更多</span>
        </div>
      </div>
    </div>
    <div v-if="images.length > 0" class="image-info">
      <p>已选择 {{ images.length }} 张图片</p>
      <button class="btn btn-secondary" @click="clearAll">清空所有</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ImageUpload',
  emits: ['update:images'],
  data () {
    return {
      images: []
    }
  },
  methods: {
    triggerFileInput () {
      this.$refs.fileInput.click()
    },
    handleFileSelect (event) {
      const files = Array.from(event.target.files)
      this.processFiles(files)
      // 清空input，允许重复选择同一文件
      event.target.value = ''
    },
    handleDrop (event) {
      const files = Array.from(event.dataTransfer.files).filter(file => file.type.startsWith('image/'))
      this.processFiles(files)
    },
    async processFiles (files) {
      for (const file of files) {
        try {
          // 检查文件大小
          const fileSizeMB = file.size / (1024 * 1024)
          if (fileSizeMB > 20) {
            alert(`文件 ${file.name} 过大 (${fileSizeMB.toFixed(1)}MB)，请选择小于20MB的图片`)
            continue
          }

          const dataUrl = await this.fileToDataUrl(file)
          this.images.push({
            file,
            dataUrl,
            name: file.name,
            size: file.size,
            sizeMB: fileSizeMB
          })

          // 给出处理提示
          if (fileSizeMB > 5) {
            console.log(`图片 ${file.name} 较大 (${fileSizeMB.toFixed(1)}MB)，将自动压缩`)
          }
          if (file.type !== 'image/jpeg' && file.type !== 'image/jpg') {
            console.log(`图片 ${file.name} 格式为 ${file.type}，将转换为JPEG格式`)
          }
        } catch (error) {
          console.error('处理文件失败:', error)
          alert(`处理文件 ${file.name} 失败: ${error.message}`)
        }
      }
      this.$emit('update:images', this.images)
    },
    fileToDataUrl (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = e => resolve(e.target.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },
    removeImage (index) {
      this.images.splice(index, 1)
      this.$emit('update:images', this.images)
    },
    clearAll () {
      this.images = []
      this.$emit('update:images', this.images)
    },
    formatFileSize (bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    }
  }
}
</script>

<style scoped>
.image-upload {
  width: 100%;
}

.upload-area {
  border: 2px dashed #dee2e6;
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.5);
}

.upload-area:hover {
  border-color: #74b9ff;
  background: rgba(116, 185, 255, 0.05);
}

.upload-placeholder {
  color: #6c757d;
}

.upload-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.upload-placeholder h3 {
  margin-bottom: 8px;
  color: #495057;
}

.size-limit {
  font-size: 13px;
  color: #74b9ff !important;
  margin-top: 8px;
  font-weight: 500;
}

.format-info {
  font-size: 12px;
  color: #28a745 !important;
  margin-top: 4px;
  font-weight: 500;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 16px;
  align-items: start;
}

.image-item {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.image-item img {
  width: 100%;
  height: 120px;
  object-fit: cover;
  display: block;
}

.remove-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  color: #dc3545;
  transition: all 0.2s ease;
}

.remove-btn:hover {
  background: #dc3545;
  color: white;
}

.image-info-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 8px;
  font-size: 12px;
  text-align: center;
}

.image-size {
  font-weight: 500;
}

.add-more {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 120px;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #6c757d;
}

.add-more:hover {
  border-color: #74b9ff;
  color: #74b9ff;
}

.add-icon {
  font-size: 24px;
  margin-bottom: 4px;
}

.image-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #dee2e6;
}

@media (max-width: 768px) {
  .image-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 12px;
  }

  .image-item img,
  .add-more {
    height: 100px;
  }
}
</style>
